'use client';

import { useState } from 'react';

import { AskForPriceDialog } from '@eliadmin/core/src/components/product/dialog/ask-for-price-dialog';
import { ProductStatusText } from '@eliadmin/core/src/components/product/product-status-text';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { useProductPriceDisplay } from '@eliadmin/core/src/components/nt/product/product-price';

import { HeroPrice } from './price';
import { PurchaseActions } from './purchase-actions';
import { HeroStock, type StockTexts } from './stock';
import { stockState, useActiveVariant } from './use-active-variant';
import { VariantChips } from './variant-chips';

export type PurchaseTexts = StockTexts & {
  /** „Ušetríte {suma}“. */
  youSave: string;
  /** „s DPH · {suma} bez DPH“. */
  priceWithoutVat: string;
};

/**
 * Buy box below the divider (Figma 112:1289 „Variant“ … „Actions“): variant chips, price, stock and
 * the purchase row of the selected variant (core variant store). A product on request offers „Opýtať
 * sa na cenu“ (core dialog) instead of the price and the cart; an ended / incoming product shows the
 * core status text.
 */
export function HeroPurchase({ texts }: { texts: PurchaseTexts }) {
  const { t } = useTranslations();
  const product = useVariantsStore(state => state.product);
  const settings = useGlobalStore(state => state.templateSettings.product_detail);
  const variant = useActiveVariant();
  const [askForPrice, setAskForPrice] = useState(false);

  const item = variant ?? product;
  const price = useProductPriceDisplay(item);
  const special = product.status === 'ended' || product.status === 'arrival';

  return (
    <div className="flex flex-col gap-6">
      <VariantChips active={variant} />
      {special ? (
        <ProductStatusText status={product.status} sizeClassName="nt-h5" />
      ) : price.priceOnRequest ? (
        <>
          <Button size="lg" className="self-start" onClick={() => setAskForPrice(true)}>
            {t('text2278')}
          </Button>
          <AskForPriceDialog
            isOpen={askForPrice}
            setIsOpen={setAskForPrice}
            product={product}
            variant={variant}
          />
        </>
      ) : (
        <>
          <HeroPrice
            price={price}
            youSave={texts.youSave}
            withoutVat={settings?.show_price_without_vat?.enabled ? texts.priceWithoutVat : ''}
          />
          {settings?.show_stock?.enabled ? (
            <HeroStock stock={stockState(item)} texts={texts} />
          ) : null}
          <PurchaseActions variant={variant} />
        </>
      )}
    </div>
  );
}
